import { IFsNodeSchema, NodeTypes } from '@cloudike/web_files'
import { IPostSearchFiles } from '@cloudike/web_files/dist/types/intarfaces/IPostSearchFiles'
import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  removeAssetFromSearchResults,
  searchActions,
  updateAssetInSearchResults
} from 'features/common/model/slice/searchSlice'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import _ from 'lodash'
import { assetsApi } from 'shared/api/assetsApi'
import { RootState } from 'shared/config/store'
import { DEFAULT_PAGE_SIZE } from 'shared/constants/common'
import { LOADING_STATUSES } from 'shared/constants/loadingStatuses'
import { getFilesSdk } from 'shared/sdk/files'
import { Filter, getDateRange } from 'shared/utils/access'
import {
  selectAllFileListItemsReducer,
  selectFileListItemReducer,
  selectFileListItemWithPressedCtrlReducer,
  selectFileListItemWithPressedShiftReducer,
  unselectAllFileListItemsReducer
} from 'shared/utils/filesListSelection'
import { getHighestLevelErrorByFieldName } from 'shared/utils/filesUtils'
import { normalizeDate } from 'shared/utils/time'
import { BoardToolbarAction } from 'shared/constants/toolbar'
import { boardsActions } from 'features/boards/model/slice/boardsSlice'
import { t } from 'i18next'

import { IGetRecentlyUploadedNodesSchema, recentlyUploaded } from '../../../../shared/api/recentlyUploaded'
import { AssetsState } from '../types/assets'
import { getOrderBy } from '../../../common/utils/itemsSort'
import { filesPreviewActions } from '../../../files-preview/model/slice/filesPreviewSlice'


export enum SortColumns {
  NAME = 'name',
  MODIFIED = 'updated',
  SIZE = 'file_info.size',
}

export enum ReplaceModalTypes {
  REPLACE = 'replace',
  KEEP_BOTH = 'keep_both'
}

export enum ReplaceModalUploadingTypes {
  DROPZONE = 'dropzone',
  FILES = 'files'
}

const availableToolbarActions = [
  {
    id: BoardToolbarAction.Download,
    name: "Download",
  },
  {
    id: BoardToolbarAction.Rename,
    name: "Rename",
  },
  {
    id: BoardToolbarAction.Delete,
    name: "Delete from library",
  },
  {
    id: BoardToolbarAction.Share,
    name: "Share",
  }
]

const adapter = createEntityAdapter<IFsNodeSchema>({
  sortComparer: (a, b) => {
    return a.name.localeCompare(b.name)
  }
})

export const assetsSelectors = adapter.getSelectors()

const initialState: AssetsState = {
  status: LOADING_STATUSES.IDLE,
  firstPageStatus: LOADING_STATUSES.IDLE,
  nextPageStatus: LOADING_STATUSES.IDLE,
  error: null,
  selectedItemsIds: [],
  lastSelectedIndexes: [],
  lastSelectedWithShiftIndex: null,
  renamingItemId: null,
  areAllAssetsFetched: false,
  availableAssetsActions: availableToolbarActions,
  systemFields: [],
  currentFolderId: '',
  currentFolder: null,
  selectedFilter: Filter.Last30Days,
}

let paginator

export const fetchFirstAssetsThunk = createAsyncThunk('assets/fetchFirstAssetsThunk', async (_, { dispatch, getState }) => {
  const sdk = getFilesSdk()
  const state = getState() as RootState
  const userState = state.user
  const assetRootBoardId = userState.assetRootBoardId || null
  const { familyFolderOperationsService: folderOperationsService } = sdk
  const pageSize = 40
  const selectedSort = state.app.selectedSort
  const sortOrder = state.app.sortOrder

  const nodesOptions = {
    embedded: true,
    preview_jwt: true,
    preview: true,
    type__in: ['file'],
    // trash_state: 'live',
    total_count: true,
    limit: 40
  }

  if(selectedSort && sortOrder) {
    nodesOptions.order_by = getOrderBy(selectedSort, sortOrder)
  }

  paginator = folderOperationsService.getChildNodesPaginator(pageSize, assetRootBoardId, nodesOptions)

  const response = await paginator.next()

  const assets = response?.data?._embedded?.nodes || []
  const filteredNodes = assets
    .filter((node: any) => !node.is_trashed)
    .filter((node: IFsNodeSchema) => node.type === NodeTypes.FILE)
  dispatch(actions.setAllAssets(filteredNodes))
  dispatch(actions.setAreAllAssetsFetched(!response.data._links.next))
})

export const fetchNextAssetsThunk = createAsyncThunk('assets/fetchNextAssetsThunk', async (_, { dispatch }) => {
  const response = await paginator.next()
  const assets = response?.data?._embedded?.nodes || []

  const filteredNodes = assets
    .filter((node: any) => !node.is_trashed)
    .filter((node: IFsNodeSchema) => node.type === NodeTypes.FILE)

  dispatch(actions.addAssets(filteredNodes))
  dispatch(actions.setAreAllAssetsFetched(!response.data._links.next))
})

// export const fetchAllAssetsThunk = createAsyncThunk('assets/fetchAllAssetsThunk', async (_, { dispatch, getState }) => {
//   const state = getState()
//   const sdk = getFilesSdk()
//   const userState = state.user
//   const assetRootBoardId = userState.assetRootBoardId || null
//   const { familyFolderOperationsService: folderOperationsService } = sdk
//   const nodesOptions = {
//     parent_id: assetRootBoardId,
//     embedded: true,
//     preview_jwt: true,
//     preview: true,
//     type__in: ['file'],
//     trash_state: 'live'
//   }
//   const delay = 100
//   const pageSize = 500
//   const allNodes = await folderOperationsService.getAllFolderContent(
//     nodesOptions,
//     pageSize,
//     delay
//   )
//   dispatch(actions.setAllAssets(allNodes))
// })

export const fetchBoardAllAssetsThunk = createAsyncThunk('assets/fetchBoardAssetsThunk',
  async ({ boardId }: { boardId: string }, { getState, dispatch }) => {
    try {
      const state = getState() as RootState
      const selectedSort = state.app.selectedSort
      const sortOrder = state.app.sortOrder
      const sdk = getFilesSdk()
      const { familyFolderOperationsService: folderOperationsService } = sdk
      const nodesOptions = {
        embedded: true,
        preview_jwt: true,
        preview: true,
        type__in: ['link'],
        // trash_state: 'live'
      }
      if(selectedSort && sortOrder) {
        nodesOptions.order_by = getOrderBy(selectedSort, sortOrder)
      }
      const delay = 100
      const pageSize = 500

      const allNodes = await folderOperationsService.getAllChildNodes(
        boardId,
        nodesOptions,
        pageSize,
        delay
      )
      const filteredNodes = allNodes
        .filter((node: IFsNodeSchema) => !node.is_trashed)
        .filter((node: IFsNodeSchema) => node.type === NodeTypes.LINK)

      dispatch(actions.setAllAssets(filteredNodes))
    } catch (error) {
      console.log('Error in fetchBoardAllAssetsThunk:', error)
      throw error
    }
  })

export const deleteOriginAssetThunk = createAsyncThunk('assets/deleteOriginAssetThunk',
  async ({ ids }: { ids: string[] }, { dispatch }) => {
    await dispatch(deleteAssetsThunk({ ids }))
    dispatch(actions.unselectAll())
    dispatch(actions.deleteItems(ids))
  })

export const getSearchAssetsThunk = createAsyncThunk(
  'assets/getSearchAssetsThunk',
  async ({ text, type, parentId }: { text?: string, type?: string, parentId?: string }) => {
    const maxCountRecentNodes = 15
    const sdk = getFilesSdk()
    const { familyFolderOperationsService: folderOperationsService } = sdk
    const currentIdRequestAllNodes = Date.now()

    const params: IPostSearchFiles = {}

    if (parentId) {
      params.parent_id = parentId
    }

    if (!!text) {
      const rsp = await folderOperationsService.getAllSearchContent({ ...params, name_pattern: `*${text}*` })
      return { data: [...rsp], id: currentIdRequestAllNodes }
    }

    if (!!type) {
      if (type === 'dir') {
        const rsp = await folderOperationsService.getAllSearchContent({ ...params, type: type })
        return { data: [...rsp], id: currentIdRequestAllNodes }
      } else {
        const rsp = await folderOperationsService.getAllSearchContent({ ...params, type: 'file', file_type: type })
        return { data: [...rsp], id: currentIdRequestAllNodes }
      }
    }
  }
)

export const renameAssetsThunk = createAsyncThunk('assets/renameAssetsThunk',
  async ({ name, assetsId }: { assetsId: string, name: string }, { dispatch, getState }) => {
    const state = getState() as RootState

    const assets = assetsSelectors.selectAll(state.assets)

    const asset = assets.find((board) => board.id === assetsId)

    const finalName = name.trim()

    if (!finalName) {
      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())

      return
    }

    if (finalName.length > 255) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'File name is too long',
        message: 'Please try again',
      })

      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())

      return
    }

    if (name === asset?.name) {
      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())

      return
    }

    try {
      const sdk = getFilesSdk()
      const { familyFolderOperationsService: folderOperationsService } = sdk
      await folderOperationsService.renameNode(assetsId as string, finalName)

      dispatch(actions.updateAsset({ id: assetsId, name: finalName }))

      dispatch(updateAssetInSearchResults({ id: assetsId, name: finalName }))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Asset renamed',
        message: 'Asset renamed successfully',
      })
    } catch (_) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
    } finally {
      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())
    }
  })

export const moveAssetsThunk = createAsyncThunk(
  "assets/moveAssetsThunk",
  async ({ ids, destinationId, items }: { ids: string[]; destinationId: string; items?: IFsNodeSchema[] },{ getState, dispatch }) => {
    const state = getState() as RootState
    const sdk = getFilesSdk()
    const { familyFolderOperationsService: folderOperationsService } = sdk
  
    let assets: IFsNodeSchema[] =
        (items?.filter(a => ids.includes(String(a.id))) as IFsNodeSchema[]) || []
  
    if (assets.length === 0) {
      assets = assetsSelectors
        .selectAll(state.assets)
        .filter(asset => ids.includes(String(asset.id)))
    }

    if (assets.length === 0 && ids.length > 0) {
      const fetched: IFsNodeSchema[] = []
      for (const id of ids) {
        try {
          const { data } = await folderOperationsService.getNodeById(id, {
            preview: true,
            preview_jwt: true,
          })
          fetched.push(data as IFsNodeSchema)
        } catch {
        }
      }
      assets = fetched
    }
  
    if (assets.length === 0) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Nothing to move',
        message: 'Could not resolve selected items. Please try again.',
      })
      throw new Error('No assets to move')
    }

    let successfullCount = 0
    let anotherFailedCount = 0
    let failedBecauseOfUniqueLinkCount = 0

    for (const asset of assets) {
      try {
        await folderOperationsService.moveNode(asset.id, destinationId, { name: asset.name, unique_link: true })
        dispatch(actions.updateAsset({ id: asset.id, parent_id: destinationId }))
        successfullCount++
      } catch (error) {
        const details = getHighestLevelErrorByFieldName(error, 'details')

        if (details?.parent_id) {
          failedBecauseOfUniqueLinkCount++

          continue
        }

        anotherFailedCount++
      }
    }

    if (successfullCount > 0) {
      if (failedBecauseOfUniqueLinkCount === 0 && anotherFailedCount === 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: t('l_notification_assetsMoved', { number: successfullCount }),
          message: '',
        })
      } else if (failedBecauseOfUniqueLinkCount > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: t('l_notification_assetsMovedMix', { number: successfullCount, added: successfullCount, notAdded: failedBecauseOfUniqueLinkCount }),
          message: '',
        })
      }

      return
    }

    if (failedBecauseOfUniqueLinkCount > 0) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: t('l_notification_assetsAlreadyOnBoard', { number: failedBecauseOfUniqueLinkCount }),
        message: '',
      })

      return
    }

    if (anotherFailedCount > 0) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
    }
  }
)

export const copyAssetsThunk = createAsyncThunk(
  "assets/copyAssetsThunk",
  async ({ ids, destinationId, items }: { ids: string[]; destinationId: string; items?: IFsNodeSchema[] },{ getState, dispatch }) => {
    const state = getState() as RootState
    const sdk = getFilesSdk()
    const { familyFolderOperationsService: folderOperationsService } = sdk

    let assets: IFsNodeSchema[] =
      (items?.filter(a => ids.includes(String(a.id))) as IFsNodeSchema[]) || []
      
    if (assets.length === 0) {
      assets = assetsSelectors
        .selectAll(state.assets)
        .filter(asset => ids.includes(String(asset.id)))
    }

    if (assets.length === 0 && ids.length > 0) {
      const fetched: IFsNodeSchema[] = []
      for (const id of ids) {
        try {
          const { data } = await folderOperationsService.getNodeById(id, {
            preview: true,
            preview_jwt: true,
          })
          fetched.push(data as IFsNodeSchema)
        } catch {
        }
      }
      assets = fetched
    }

    if (assets.length === 0) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Nothing to copy',
        message: 'Could not resolve selected items. Please try again.',
      })
      throw new Error('No assets to copy')
    }

    let successfullCount = 0
    let failedBecauseOfUniqueLinkCount = 0
    let anotherFailedCount = 0

    for (const asset of assets) {
      const id = asset.id
      const linkedNodeId =
          (asset as any).linked_node_id ??
          (asset as any)?.fields?.linked_node_id ??
          id

      try {
        await folderOperationsService.copyFileByFileSource(
          id,
          destinationId,
          { type: 'link', linked_node_id: linkedNodeId, unique_link: true }
        )

        dispatch(actions.addAsset({ ...asset, id, parent_id: destinationId }))

        successfullCount++
      }
      catch (error) {
        const details = getHighestLevelErrorByFieldName(error, 'details')

        if (details?.linked_node_id) {
          failedBecauseOfUniqueLinkCount++

          continue
        }

        anotherFailedCount++
      }
    }

    if (successfullCount > 0) {
      if (failedBecauseOfUniqueLinkCount === 0 && anotherFailedCount === 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: t('l_notification_assetsAdded', { number: successfullCount }),
          message: '',
        })
      } else if (failedBecauseOfUniqueLinkCount > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: t('l_notification_assetsAddedMix', { number: successfullCount, added: successfullCount, notAdded: failedBecauseOfUniqueLinkCount }),
          message: '',
        })
      }

      return
    }

    if (failedBecauseOfUniqueLinkCount > 0) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: t('l_notification_assetsAlreadyOnBoard', { number: failedBecauseOfUniqueLinkCount }),
        message: '',
      })

      return
    }

    if (anotherFailedCount > 0) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
    }
  }
)

export const copyAssetsFromPersonalCloudThunk = createAsyncThunk(
  "assets/copyAssetsFromPersonalCloudThunk",
  async ({ asset, destinationId }: { asset: IFsNodeSchema, destinationId: string }) => {
    const sdk = getFilesSdk()

    try {

      const {
        data: {
          ids: [newNodeId],
        },
      } = await sdk.boardsService.getNodeIds(1, {})

      const params = {
        id: newNodeId,
        name: asset.name,
        type: NodeTypes.FILE,
        file_source: asset._links.self.href,
        parent_id: destinationId,
      }


      await sdk.boardsService.createNewNode(params)

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Asset saved',
        message: 'Asset saved successfully',
      })
    } catch (_) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
      throw new Error('Something went wrong with coping assets')
    }
  }
)

export const deleteAssetsThunk = createAsyncThunk(
  'assets/deleteAssetsThunk',
  async ({ ids, linkedIds, needNotify = true }: { ids: string[], linkedIds?: string[], needNotify?: boolean }, { dispatch }) => {
    // ids are ids of 'link' types nodes
    // linkedIds are ids of 'file' types nodes
    try {
      const originIds = linkedIds || ids
      const sdk = getFilesSdk()
      const { familyFolderOperationsService: folderOperationsService } = sdk
      if(originIds.length <= 100) {
        await folderOperationsService.moveNodesToTrash(originIds)
      } else {
        const delay = originIds.length * 5
        await folderOperationsService.batchMovingNodesToTrash(originIds, undefined, delay)
      }
      dispatch(actions.unselectAll())
      dispatch(actions.deleteItems(ids))
      dispatch(removeAssetFromSearchResults(ids))
      dispatch(searchActions.unselectAll())

      if(needNotify) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: `${ids.length} ${ids.length === 1 ? 'item' : 'items'} deleted`,
          message: 'Items deleted successfully',
        })
      }
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
    }
  }
)

export const downloadAssetThunk = createAsyncThunk(
  'assets/downloadAssetThunk',
  async ({ id, isPersonal }: { id: string, isPersonal?: boolean }) => {
    const sdk = getFilesSdk()

    if (isPersonal) {
      const { folderOperationsService } = sdk

      const link = await folderOperationsService.getDownloadLink(id)
      window.location.href = link

      return
    }

    const { familyFolderOperationsService: folderOperationsService } = sdk
    const link = await folderOperationsService.getDownloadLink(id)

    window.location.href = link
  }
)

export const downloadAssetsThunk = createAsyncThunk(
  'assets/downloadAssetsThunk',
  async ({ ids }: { ids: string[] }, { dispatch, getState }) => {
    const state = getState() as RootState
    const nodes = assetsSelectors.selectAll(state.assets)

    try {
      const sdk = getFilesSdk()
      const { familyFolderOperationsService: folderOperationsService } = sdk
      let link

      if (ids.length === 1) {
        const nodeType = nodes.find(node => node.id === ids[0])?.type

        if (nodeType === NodeTypes.DIR) {
          link = await folderOperationsService.getZipStreamLink(ids)
        } else {
          link = await folderOperationsService.getDownloadLink(ids[0])
        }
      } else {
        link = await folderOperationsService.getZipStreamLink(ids)
      }

      window.location.href = link
    } catch (error) {
      const details = getHighestLevelErrorByFieldName(error, 'details')

      if (details?.node_ids === 'Longer than maximum length 100') {
        // showNotification({
        //   type: NOTIFICATION_TYPES.WARNING,
        //   title: t('l_notification_downloadFilesError')
        // })

        return
      }

      // showNotification({
      //   type: NOTIFICATION_TYPES.WARNING,
      //   title: t('l_notification_somethingWrongTryAgain')
      // })
    } finally {
      dispatch(actions.unselectAll())
    }
  }
)

export const renameAssetThunk = createAsyncThunk(
  'assets/renameAssetThunk',
  async ({ name }: { name: string }, { getState, dispatch }) => {
    const state = getState() as RootState
    const currentFolderId = state.assets.currentFolderId

    const nodes = assetsSelectors.selectAll(state.assets)

    const renamingItemId = state.assets.renamingItemId
    const node = nodes.find(node => node.id === renamingItemId)
    const nodeType = node?.type

    const finalName = name.trim()

    if (!finalName) {
      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())

      return
    }

    if (finalName.length > 255) {
      // showNotification({
      //   type: NOTIFICATION_TYPES.WARNING,
      //   title: t('l_notification_fileNameLongError')
      // })

      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())

      return
    }

    if (name === node?.name) {
      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())

      return
    }

    try {
      const sdk = getFilesSdk()
      const { familyFolderOperationsService: folderOperationsService } = sdk
      await folderOperationsService.renameNode(renamingItemId, finalName)

      // dispatch(actions.updateItem({ id: renamingItemId, name: finalName }))

      // if (nodeType === NodeTypes.DIR) {
      //   showNotification({
      //     type: NOTIFICATION_TYPES.SUCCESS,
      //     title: t('l_notification_folderRenamed')
      //   })
      // } else {
      //   showNotification({
      //     type: NOTIFICATION_TYPES.SUCCESS,
      //     title: t('l_notification_fileRenamed')
      //   })
      // }
    } catch (error) {
      // showNotification({
      //   type: NOTIFICATION_TYPES.WARNING,
      //   title: t('l_notification_somethingWrongTryAgain')
      // })
    } finally {
      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())
    }
  }
)

export const renameAssetByIdThunk = createAsyncThunk(
  'assets/renameAssetByIdThunk',
  async ({ id, name, callback }: { id: string, name: string, callback?: () => void }, { getState, dispatch }) => {

    const sdk = getFilesSdk()
    const { familyFolderOperationsService: folderOperationsService } = sdk
    await folderOperationsService.renameNode(id, name)

    if (callback) {
      callback()
    }
  }
)

// export const uploadAssetsWithDropzone = createAsyncThunk(
//   'assets/uploadAssetsWithDropzone',
//   async ({ files }: { files: (File & {path: string })[] }, { getState, dispatch }) => {
//     const state = getState() as RootState
//     const catalogId = state.assets.currentFolderId
//
//     dispatch(uploadFilesThunk({ files, symlinkParentId: catalogId }))
//   }
// )

export const fetchSystemFieldsThunk = createAsyncThunk(
  'assets/fetchSystemFieldsThunk',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const user = state.user.user

    const systemFields = await assetsApi.getSystemFields(user.family_user_id)

    const rawFields = systemFields?._embedded?.fields ?? []

    const fields = rawFields.map((field: any) => ({
      ...field,
      options: Array.isArray(field?.options)
        ? field.options.map((option: any) => {
          const opt =
                option && typeof option === 'object'
                  ? option
                  : { id: option, value: String(option) }
          return {
            ...opt,
            type: field.title === 'status' ? 'status' : 'tag',
          }
        })
        : [],
    }))

    dispatch(actions.setSystemFields(fields))
  }
)


export const setFieldOnAssetThunk = createAsyncThunk(
  'assets/setFieldOnAssetThunk',
  async ({ id, fieldID, value, successCallback }: { id: string, fieldID: string, value: string[] | string | boolean, successCallback?: () => void }, { getState }) => {
    const state = getState() as RootState
    const user = state.user.user

    await assetsApi.setFieldOnAsset(user.family_user_id, id, fieldID, value)

    if (successCallback) {
      successCallback()
    }
  }
)

export const deleteAssetsFieldThunk = createAsyncThunk(
  'assets/deleteAssetsFieldThunk',
  async ({ nodeId, fieldID, signal }: { nodeId: string, fieldID: string, signal: any }, { getState }) => {
    const state = getState() as RootState
    const user = state.user.user
    await assetsApi.deleteAssetFields(user.family_user_id, nodeId, fieldID, signal)
  }
)

export const updateFieldThunk = createAsyncThunk(
  'assets/updateFieldThunk',
  async ({ fieldID, data, successCallback }: { fieldID: string, data: any, successCallback?: () => void }, { getState }) => {
    const state = getState() as RootState
    const user = state.user.user

    await assetsApi.updateField(user.family_user_id, fieldID, data)

    if (successCallback) {
      successCallback()
    }
  }
)

export const updateSystemFieldThunk = createAsyncThunk(
  'assets/updateSystemFieldThunk',
  async ({ fieldID, data, successCallback }: { fieldID: string, data: any, successCallback?: (newTags: any) => void }, { getState, dispatch }) => {
    const state = getState() as RootState
    const user = state.user.user

    const response = await assetsApi.updateSystemField(user.family_user_id, fieldID, data)

    if (successCallback) {
      successCallback(response)
      dispatch(boardsActions.setSystemFieldsValues({ tags: response.options, statusOptions: null }))
    }
  }
)

export const updateAssetThunk = createAsyncThunk(
  'assets/updateAssetThunk',
  async ({ id, data, successCallback }: { id: string, data: any, successCallback?: () => void }, { getState }) => {
    const state = getState() as RootState
    const user = state.user.user

    await assetsApi.updateAsset(user.family_user_id, id, data)

    if (successCallback) {
      successCallback()
    }
  }
)

export const resetAssetsStateThunk = createAsyncThunk(
  'assets/resetAssetsStateThunk',
  async (_, { dispatch }) => {
    dispatch(actions.resetState())
  }
)

export const fetchAllRecentlyUploadedAssetsThunk = createAsyncThunk(
  'assets/fetchRecentlyUploadedAssets',
  async ({ createdFrom }: { createdFrom: string }, { getState, dispatch }) => {
    const state = getState()
    const user = state.user.user
    const familyUserId = user.family_user_id
    if(!familyUserId) return
    const params: IGetRecentlyUploadedNodesSchema  = {
      embedded: true,
      preview: true,
      preview_jwt: true,
    }
    if(createdFrom) {
      params.file_info__created__gte = createdFrom
    }
    const allNodes = await recentlyUploaded.getAllRecentlyUploaded(familyUserId, params)
    dispatch(actions.setAllAssets(allNodes))
  }
)

export const loadAllAssetsInBackgroundThunk = createAsyncThunk(
  'assets/loadAllAssetsInBackground',
  async ({ currentFileId, callback }: { currentFileId: string, callback: (items: any[], currentIndex: number) => void }, { getState }) => {
    const state = getState()
    const sdk = getFilesSdk()
    const userState = state.user
    const assetRootBoardId = userState.assetRootBoardId || null
    const { familyFolderOperationsService: folderOperationsService } = sdk

    const nodesOptions = {
      embedded: true,
      preview_jwt: true,
      preview: true,
      // type__in: ['file'],
      // trash_state: 'live'
    }

    const delay = 100
    const pageSize = 500
    const allNodes = await folderOperationsService.getAllChildNodes(
      assetRootBoardId,
      nodesOptions,
      pageSize,
      delay
    )
    const filteredNodes = allNodes
      .filter((node: any) => !node.is_trashed)
      .filter((node: IFsNodeSchema) => node.type === NodeTypes.FILE)
    const currentIndex = allNodes.findIndex(node => node.id === currentFileId)

    if (currentIndex !== -1 && allNodes.length > 1) {
      callback(filteredNodes, currentIndex)
    }
  }
)

let recentlyUploadedIterator = null

export const createRecentlyUploadedIterator = createAsyncThunk(
  'assets/updateRecentlyUploadedIterator', async ({ createdGte, createdLte,  pageSize = 40, signal }: { createdGte?: string, createdLte?: string, pageSize?: number, signal?: AbortSignal }, { getState }) => {
    const state = getState() as RootState
    const user = state.user.user
    const familyUserId = user.family_user_id
    if(!familyUserId) return
    const params: IGetRecentlyUploadedNodesSchema = {
      embedded: true,
      preview: true,
      preview_jwt: true,
    }
    if(createdGte) {
      params.file_info__created__gte = createdGte
    }
    if(createdLte) {
      params.file_info__created__lte = createdLte
    }
    if(pageSize) {
      params.limit = pageSize
    }
    recentlyUploadedIterator = recentlyUploaded.getAllRecentlyUploadedIterator(familyUserId, params, signal)
  })

export const fetchFirstRecentlyUploadedThunk = createAsyncThunk('assets/fetchFirstRecentlyUploadedThunk',
  async (_, { dispatch }) => {
    if (!recentlyUploadedIterator) {
      return
    }
    const response = await recentlyUploadedIterator[Symbol.asyncIterator]().next()
    const { value, done } = response
    dispatch(actions.setAllAssets(value))
    dispatch(actions.setAreAllAssetsFetched(done))
  })

export const fetchNextRecentlyUploadedThunk = createAsyncThunk('assets/fetchNextRecentlyUploadedThunk', async (_, { dispatch }) => {
  if(!recentlyUploadedIterator) {
    return
  }
  const response = await recentlyUploadedIterator[Symbol.asyncIterator]().next()
  const { value, done } = response
  dispatch(actions.addAssets(value))
  dispatch(actions.setAreAllAssetsFetched(done))
})

export const loadRecentlyUploadedAssetsInBackgroundThunk = createAsyncThunk(
  'assets/loadRecentlyUploadedAssetsInBackgroundThunk',
  async ({ currentFileId, callback }: { currentFileId: string, callback: (items: any[], currentIndex: number) => void }, { getState, dispatch }) => {
    const state = getState() as RootState
    const filter = state.assets.selectedFilter
    const [dateFrom] = getDateRange(filter)
    const createdGte = normalizeDate(dateFrom)
    const pageSize = DEFAULT_PAGE_SIZE

    try {
      await dispatch(createRecentlyUploadedIterator({
        createdGte,
        pageSize
      })).unwrap()

      await dispatch(fetchFirstRecentlyUploadedThunk()).unwrap()

      const updatedState = getState() as RootState
      const assets = assetsSelectors.selectAll(updatedState.assets)

      if (assets.length > 1) {
        const currentIndex = assets.findIndex(asset => asset.id === currentFileId)
        if (currentIndex !== -1) {
          callback(assets, currentIndex)
        }
      }
    } catch (error) {
      console.error('Background loading failed:', error)
    }
  }
)

export const fetchNodeById = createAsyncThunk('assets/fetchNodeById', async ({ nodeId }:{nodeId: string}) => {
  const sdk = getFilesSdk()
  const params = { preview: true,
    preview_jwt: true }
  const { familyFolderOperationsService: folderOperationsService } = sdk
  const { data } = await folderOperationsService.getNodeById(nodeId, params)
  return data
})

export const addNewAssetThunk = createAsyncThunk('assets/addNewAssetThunk',
  async ({ nodeId }: { nodeId: string }, { dispatch }) => {
    const data = await dispatch(fetchNodeById({ nodeId })).unwrap()
    dispatch(actions.addAsset(data))
    dispatch(filesPreviewActions.addItem(data as any))
  })

export const addNewAssetWithUpdatedFieldsThunk = createAsyncThunk('assets/addNewAssetWithUpdatedFieldsThunk',
  async ({ nodeId, fields }: { nodeId: string, fields: any }, { dispatch }) => {
    const data = await dispatch(fetchNodeById({ nodeId })).unwrap()
    dispatch(actions.addAsset({ ...data, ...fields }))
    dispatch(filesPreviewActions.addItem({ ...data, ...fields } as any))
  })

/**
 * Enriches an asset with data from the original file if it's a link without preview
 * @param newAsset - new asset to add
 * @param allAssets - all existing assets
 * @returns enriched asset or original asset
 */
const enhanceAssetWithOriginalFileData = (newAsset: any, allAssets: any[]): any => {
  // Get linked_node_id from asset fields (if it's a link node)
  const linkedNodeId = _.get(newAsset, 'fields.linked_node_id') as string | undefined

  // Check if the link node has a preview
  const hasPreview = (asset: any): boolean => {
    return !_.isEmpty(asset?._embedded?.previews)
  }

  const id = _.get(newAsset, 'node_id', null)
  const authorId = _.get(newAsset, 'author.id', null)
  const authorName = _.get(newAsset, 'author.name', null)
  const fileInfo = _.get(newAsset, 'fields.file_info', null)

  // If this is a link node and it doesn't have a preview, try to find the original file
  if (linkedNodeId && !hasPreview(newAsset)) {
    // Find the original file by ID
    const originalFile = allAssets.find(asset => asset.node_id === linkedNodeId)
    if (originalFile && hasPreview(originalFile)) {
      // Copy file information and preview from the original file
      const fileInfo = _.get(originalFile, 'fields.file_info', {})
      const embedded = _.get(originalFile, '_embedded', {})
      const currentVersionId = _.get(originalFile, '_embedded.current_version_id', null)
      // Create an updated asset with information from the original file
      const resultNode = {
        ...newAsset,
        id,
        description: '',
        is_explicitly_trashed: false,
        is_shared: false,
        author_id : authorId,
        author_name: authorName,
        last_editor_id: authorId,
        linked_node_id: linkedNodeId,
        linked_node_is_trashed: false,
        current_version_id : currentVersionId,
        file_info: fileInfo,
        _embedded: embedded
      }
      if(resultNode.action) {
        delete resultNode.action
      }
      if(resultNode.state) {
        delete resultNode.state
      }
      return resultNode
    }
  }

  // Return the original asset if enrichment is not required
  return {
    ...newAsset,
    id,
    author_name: authorName,
    author_id : authorId,
    description: '',
    is_explicitly_trashed: false,
    is_shared: false,
    last_editor_id: authorId,
    linked_node_id: linkedNodeId,
    linked_node_is_trashed: false,
    file_info: fileInfo,
  }
}

export const assetsSlice = createSlice({
  name: 'assets',
  initialState: adapter.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload
    },
    addAsset: (state, action) => {
      adapter.addOne(state, action.payload)
    },
    addAssets: (state, action) => {
      adapter.addMany(state, action.payload)
    },
    setAllAssets: (state, action) => {
      adapter.setAll(state, action.payload)
    },
    updateAsset: (state, action: PayloadAction<Partial<IFsNodeSchema>>) => {
      adapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload
      })
    },
    updateAssets: (state, action) => {
      adapter.updateMany(state, action.payload)
    },
    deleteAsset: (state, action: PayloadAction<string>) => {
      adapter.removeOne(state, action.payload)
    },
    removeAllAssets: (state) => {
      adapter.removeAll(state)
    },
    addNewAsset: (state, action) => {
      const newAsset = action.payload
      const allAssets = assetsSelectors.selectAll(state)
      const enhancedAsset = enhanceAssetWithOriginalFileData(newAsset, allAssets)
      adapter.addOne(state, enhancedAsset)
    },
    selectItem: selectFileListItemReducer(assetsSelectors),
    selectItemWithPressedCtrl: selectFileListItemWithPressedCtrlReducer(),
    selectItemWithPressedShift: selectFileListItemWithPressedShiftReducer(assetsSelectors),
    selectAll: selectAllFileListItemsReducer,
    unselectAll: unselectAllFileListItemsReducer,
    setSelectedItemsIds: (state, action: PayloadAction<string[]>) => {
      state.selectedItemsIds = action.payload
    },
    deleteItems: (state, action: PayloadAction<string[]>) => {
      adapter.removeMany(state, action.payload)
    },
    setRenamingItemId: (state, action) => {
      state.renamingItemId = action.payload
    },
    resetState: (state) => {
      adapter.removeAll(state)
      state.status = LOADING_STATUSES.IDLE
      state.error = null
      state.selectedItemsIds = []
      state.lastSelectedWithShiftIndex = null
      state.lastSelectedIndexes = []
      state.currentFolderId = ''
      state.currentFolder = null
    },
    setAreAllAssetsFetched: (state, action) => {
      state.areAllAssetsFetched = action.payload
    },
    setSystemFields: (state, action) => {
      state.systemFields = action.payload
    },
    setSelectedFilter: (state, action) => {
      state.selectedFilter = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBoardAllAssetsThunk.pending, (state) => {
      state.status = LOADING_STATUSES.LOADING
    })
      .addCase(fetchBoardAllAssetsThunk.fulfilled, (state) => {
        state.status = LOADING_STATUSES.SUCCEEDED
      })
      .addCase(fetchBoardAllAssetsThunk.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED
      })

    builder
      .addCase(fetchFirstRecentlyUploadedThunk.pending, (state) => {
        state.firstPageStatus = LOADING_STATUSES.LOADING
      })
      .addCase(fetchFirstRecentlyUploadedThunk.fulfilled, (state) => {
        state.firstPageStatus = LOADING_STATUSES.SUCCEEDED
      })
      .addCase(fetchFirstRecentlyUploadedThunk.rejected, (state) => {
        state.firstPageStatus = LOADING_STATUSES.FAILED
      })

    builder
      .addCase(fetchNextRecentlyUploadedThunk.pending, (state) => {
        state.nextPageStatus = LOADING_STATUSES.LOADING
      })
      .addCase(fetchNextRecentlyUploadedThunk.fulfilled, (state) => {
        state.nextPageStatus = LOADING_STATUSES.SUCCEEDED
      })
      .addCase(fetchNextRecentlyUploadedThunk.rejected, (state) => {
        state.nextPageStatus = LOADING_STATUSES.FAILED
      })

  }
})

export default assetsSlice.reducer

const {
  reducer, actions
} = assetsSlice

export { actions as assetsActions, reducer as assetsReducer }

